// FollowUpPanel — the Follow-Up Enforcement Engine dashboard.
//
// One call, one panel:
//   · Morning brief (top)
//   · Today's 2-calls compliance ring
//   · Priority-ranked leads with 🔥 reasons
//   · Missed calls list
//   · Appointments pending confirmation
//   · "Scan for hot leads" button (runs positive-response scan)
//
// Data source: POST /api/follow-up/today (composed snapshot)
// Bulk scan: POST /api/follow-up/scan-hot

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  try {
    const t = readPersistedJwtDirect();
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

function Card({ title, right, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      {title && (
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">{title}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function ComplianceRing({ pct, required, completed }) {
  const c = pct >= 90 ? { fg: 'text-emerald-800', bg: 'bg-emerald-100' }
          : pct >= 60 ? { fg: 'text-lime-800',    bg: 'bg-lime-100' }
          : pct >= 30 ? { fg: 'text-amber-800',   bg: 'bg-amber-100' }
          :             { fg: 'text-red-800',     bg: 'bg-red-100' };
  return (
    <div className={`${c.bg} rounded-2xl p-4 flex items-baseline gap-4`}>
      <div className={`text-4xl font-black ${c.fg}`}>{pct}<span className="text-lg text-slate-500">%</span></div>
      <div className="text-[11px] text-slate-700">
        <div className="font-semibold">Follow-up compliance</div>
        <div>{completed} of {required} calls made today</div>
      </div>
    </div>
  );
}

export default function FollowUpPanel({ onSelectLead = null, onGotoInbox = null }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  const [scanning, setScanning]   = React.useState(false);
  const [scanResult, setScanResult] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await bearer();
      const r = await fetch('/api/follow-up/today', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ config: { calls_per_day: 2, cutoff_hour: 18 } }),
      });
      const j = await r.json();
      if (r.ok) setData(j);
      else setError(j.error || `HTTP ${r.status}`);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const scanForHot = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const headers = await bearer();
      const r = await fetch('/api/follow-up/scan-hot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ window_days: 7 }),
      });
      const j = await r.json();
      setScanResult(r.ok ? j : { error: j.error || `HTTP ${r.status}` });
      if (r.ok) load(); // refresh snapshot after scan
    } catch (e) {
      setScanResult({ error: e.message });
    } finally { setScanning(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading follow-up dashboard…</div>;
  if (error)   return <div className="p-6 text-sm text-red-700">Failed to load: {error}</div>;
  if (!data)   return null;

  const brief = data.morning_brief || {};
  const comp  = data.compliance || {};
  const ranking = data.priority_ranking || [];
  const missed  = comp.missed_leads || [];
  const appts   = data.appointments_pending_confirmation || [];

  return (
    <div className="max-w-none mx-auto p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Follow-Up Engine</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">Today's focus</h2>
        </div>
        <button
          onClick={scanForHot}
          disabled={scanning}
          className={`text-xs font-semibold px-3 py-1.5 rounded ${
            scanning ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 text-white hover:bg-slate-700'
          }`}
        >
          {scanning ? 'Scanning…' : 'Scan messages for hot leads'}
        </button>
      </div>

      {scanResult && (
        <Card>
          {scanResult.error ? (
            <div className="text-xs text-red-700">Scan failed: {scanResult.error}</div>
          ) : (
            <div className="text-xs text-slate-700">
              Scanned {scanResult.scanned_lead_count} leads · {scanResult.newly_hot?.length || 0} newly flagged HOT
              {scanResult.already_hot_count > 0 && ` · ${scanResult.already_hot_count} already HOT`}
            </div>
          )}
        </Card>
      )}

      {/* Morning brief */}
      <Card title="AI morning brief">
        {brief.bullets?.length > 0 ? (
          <ul className="space-y-1">
            {brief.bullets.map((b, i) => (
              <li key={i} className="text-xs text-slate-800 flex gap-2 items-start">
                <span className="flex-shrink-0">{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs text-slate-500 italic">{brief.headline}</div>
        )}
      </Card>

      {/* Compliance ring */}
      <ComplianceRing pct={comp.compliance_pct} required={comp.calls_required} completed={comp.calls_completed} />

      {/* Priority ranking */}
      <Card
        title={`Priority ranking (${ranking.length})`}
        right={ranking.length > 0 && <span className="text-[10px] text-slate-500">Sorted by score</span>}
      >
        {ranking.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No hot leads or appointments today.</div>
        ) : (
          <ul className="space-y-2">
            {ranking.slice(0, 20).map((r) => (
              <li key={r.lead_id} className="border border-slate-100 rounded p-2">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-bold text-slate-900">{r.name || 'Unnamed lead'}</span>
                    <span className="text-[10px] text-slate-500 tabular-nums">score {r.score}</span>
                  </div>
                  {onSelectLead && (
                    <button
                      onClick={() => onSelectLead(r.lead_id)}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-900 text-white hover:bg-slate-700"
                    >
                      Open lead
                    </button>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {r.all_reasons.map((rz, i) => (
                    <span key={i} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      i === 0 ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {rz.emoji} {rz.label}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[10px] text-slate-500">
                  Calls today: {r.calls_today}
                  {r.appt_today_at && ` · appt at ${new Date(r.appt_today_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Missed calls */}
      {missed.length > 0 && (
        <Card title={`Missed follow-ups (${missed.length})`} right={comp.overdue && <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded uppercase">Overdue</span>}>
          <ul className="space-y-1">
            {missed.slice(0, 15).map((m) => (
              <li key={m.lead_id} className="text-xs text-slate-800 flex justify-between items-baseline">
                <span>{m.name || 'Unnamed'} <span className="text-slate-500">— {m.calls_today} / {m.calls_today + m.calls_needed} calls</span></span>
                {onSelectLead && (
                  <button
                    onClick={() => onSelectLead(m.lead_id)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    Open
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Appointments pending confirmation */}
      {appts.length > 0 && (
        <Card title={`Appointments pending confirmation (${appts.length})`}>
          <ul className="space-y-1.5">
            {appts.slice(0, 15).map((a) => (
              <li key={a.id} className="text-xs text-slate-800 flex justify-between items-baseline gap-2">
                <div>
                  <div className="font-semibold">{a.client_name || 'Unnamed'}</div>
                  <div className="text-[10px] text-slate-500">
                    {new Date(a.appointment_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    · in {a.hours_until}h
                    · {a.reminder_24h_sent ? '24h reminder sent' : 'no reminders sent'}
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                  a.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' :
                  a.hours_until < 24       ? 'bg-red-100 text-red-800' :
                                             'bg-amber-100 text-amber-800'
                }`}>
                  {a.status}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
